import type { PosDiscountType } from "@/type/pos";

/**
 * The one place a POS discount is capped.
 *
 * ⚠️ THIS GUARDS A REAL BUG, NOT A DISPLAY DETAIL. The page caps a percentage at
 * 100 when it computes the discount, so typing 500 *looked* like a 100% discount
 * on screen — but the raw value is what goes on the wire, and the server computes
 * the discount itself. A till showing "free" while asking the backend to take 500%
 * off is not a display bug.
 *
 * It lives here because there are now two ways to enter a discount — the summary's
 * own field and the on-screen keypad — and two copies of a cap that guards the same
 * overflow would eventually disagree. The rule matches what the accounting editor
 * already applies (`SalesInvoiceDiscountEditor`): a percentage cannot exceed 100,
 * and a fixed amount cannot exceed what is owed.
 *
 * ⚠️ Switching Fixed → Percentage must re-run this. A 5,000 fixed discount left
 * alone becomes 5,000 **percent** the moment the type flips — the same overflow by
 * a different route.
 */
export function clampDiscountValue(
  type: PosDiscountType,
  value: unknown,
  subtotalAmount: unknown,
): number {
  const numeric = Math.max(Number(value ?? 0), 0);

  if (!Number.isFinite(numeric))
    return 0;

  return type === "Percentage"
    ? Math.min(numeric, 100)
    : Math.min(numeric, Math.max(Number(subtotalAmount ?? 0), 0));
}

/**
 * Our word for a flat discount, in the word every backend door actually takes.
 *
 * ⚠️ THE VOCABULARY DIFFERS FROM OURS. This app says `"Fixed"`; the server's
 * `discount_type` takes `"Amount"`. Sent unmapped it is an unrecognised type, and the
 * two doors disagree about what that means: `create_pos_order` / `update_pos_order`
 * REFUSE the request outright ("discount_type must be Percentage (0–100) or Amount"),
 * while `settle_open_invoice` accepts it and silently drops the discount — the till
 * shows a reduced total and prints it on the receipt while the invoice is submitted
 * at full value. `"Percentage"` is the same word on both sides.
 *
 * It lives here beside the cap because the translation was written once inside
 * `posApi` and then not applied to the other three doors, which is exactly how the
 * driver-order refusal and the settlement's silent drop happened. One copy.
 *
 * ⚠️ CALLED AT THE API BOUNDARY, NEVER IN A COMPONENT. The UI, the receipt and the
 * sale slots keep the app's own vocabulary; only the wire uses this.
 */
export function toBackendDiscountType(
  type: PosDiscountType | string | null | undefined,
): "Percentage" | "Amount" | undefined {
  const value = String(type ?? "").trim();

  if (!value)
    return undefined;

  return value === "Percentage" ? "Percentage" : "Amount";
}

/**
 * The ceiling a numeric entry should offer for a discount of this type.
 *
 * Used by the keypad so the operator is stopped at the cap while typing, rather
 * than typing 500 and having it silently become 100 when they confirm.
 */
export function discountValueCeiling(type: PosDiscountType, subtotalAmount: unknown): number {
  return type === "Percentage" ? 100 : Math.max(Number(subtotalAmount ?? 0), 0);
}
